/**
 * Shared section header for the Net Impact drill-in panel — PLT-4915.
 *
 * A one-line title on the left, optional right-aligned valence-tinted
 * figure on the right, sitting on a thin gray-200 underline. Used by:
 *   - Everyday units tab — once per valence (positive / negative block)
 *   - Positive contributors tab — once at the top of the list
 *   - Negative contributors tab — once at the top of the list
 *
 * Factored out in Janne review v5: the review asked us to "take the
 * header style from Everyday units and apply the same style to
 * Positive/Negative as well", so the three places that need this header
 * now share a single source of truth and a single set of tokens.
 *
 * Wording convention (kept identical across tabs):
 *   `{entity}'s {valence} {category} impact` (e.g. "Siemens's positive
 *   physical diseases impact"). The right-aligned figure is the per-
 *   valence cents-per-dollar value with a leading sign — teal for
 *   positive, purple for negative.
 */
import { Valence } from '@upright/common/model/types';
import { ReactNode } from 'react';
import styled from 'styled-components';

interface Props {
  /** Left-aligned title. Use a plain string — the component handles weight + color. */
  title: ReactNode;
  /** Optional right-aligned figure (e.g. "+1.27 c/$"). Omit for header-only sections. */
  figure?: ReactNode;
  /** Valence tints the right-aligned figure (teal-700 for POSITIVE, purple-700 for NEGATIVE,
   *  gray-900 otherwise). Ignored if `figure` is not provided. */
  figureValence?: Valence;
}

export function DrillInSectionHeader({ title, figure, figureValence }: Props) {
  return (
    <Root>
      <Title>{title}</Title>
      {figure !== undefined && <Figure $valence={figureValence ?? Valence.OTHER}>{figure}</Figure>}
    </Root>
  );
}

const Root = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--space__md);
  padding-bottom: var(--space__sm);
  border-bottom: 1px solid var(--color__gray-200);
`;

const Title = styled.div`
  flex: 1;
  min-width: 0;
  font-size: var(--font-size__md);
  font-weight: var(--font-weight__bold);
  color: var(--color__gray-900);
  line-height: 1.3;
  text-wrap: pretty;
`;

const Figure = styled.div<{ $valence: Valence }>`
  font-size: var(--font-size__md);
  font-weight: var(--font-weight__bold);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  color: ${p =>
    p.$valence === Valence.POSITIVE
      ? 'var(--color__teal-700)'
      : p.$valence === Valence.NEGATIVE
        ? 'var(--color__purple-700)'
        : 'var(--color__gray-900)'};
`;
